// BP-83 for INPUTS: a date field is an MUI X DatePicker in the TENANT's date
// format, never a native <TextField type="date"> (that shows the browser's
// locale — mm/dd/yyyy on a US machine — whatever the tenant is set to).
//
// For the common case where the form holds the date as the wire string
// ("YYYY-MM-DD"): the value goes in as that string and comes out as that
// string, so a caller swaps a native input for this without touching its
// state, its payload or its validation. The format only changes what the user
// sees and types.
//
//   <TenantDatePicker label="From" value={fromDate} onChange={setFromDate} />
//
// - `value` "" / null / the MySQL zero-date → an empty field.
// - `onChange` gets "YYYY-MM-DD", or "" when the field is cleared. A
//   half-typed date is NOT reported (the field keeps the keystrokes; the form
//   keeps its last whole date), so a form never saves "Invalid Date".
// - The pattern is useTenantDateFormat(), dd-MMM-yyyy until the config loads.
// - It works with or without a LocalizationProvider above it. The app root
//   supplies one (src/index.tsx, AdapterDateFns, no locale); outside it — a
//   test, a portal, a Storybook story — the picker brings the same adapter
//   rather than throwing "Can not find utils in context" (H221).
// - In a grid's inline editor (renderEditCell) pass placement="gridCell": borderless, it
//   fills the cell or stacked line instead of being clipped by it (BP-83, C20 2026-10-12;
//   with no theme margin, A824 2026-10-14). Pinned by TenantDatePickerGridCellConformance.test.
import React, { useContext, useEffect, useState } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider, MuiPickersAdapterContext } from "@mui/x-date-pickers/LocalizationProvider";
import { format, isValid } from "date-fns";
import { useField } from "formik";

import { DEFAULT_TENANT_DATE_FORMAT, tenantDateValue, useTenantDateFormat } from "../utils/tenantDate";

export interface TenantDatePickerProps {
  label?: React.ReactNode;
  /** The date as the wire carries it: "YYYY-MM-DD" (a longer value keeps its day). */
  value: string | null | undefined;
  /** "YYYY-MM-DD", or "" when cleared. */
  onChange: (iso: string) => void;
  minDate?: string | null;
  maxDate?: string | null;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  error?: boolean;
  helperText?: React.ReactNode;
  /** Position the desktop calendar beside its button. */
  anchorToIcon?: boolean;
  /** Anything else for the underlying TextField (sx, inputProps, name, …). */
  textFieldProps?: Partial<TextFieldProps>;
  /**
   * Where the field sits:
   *   "form"      a form field: the outlined TextField (default)
   *   "gridCell"  a grid's inline editor (renderEditCell): borderless and dense, it fills the
   *               cell -- or the one stacked line (BP-106) -- it edits, as the grid's own
   *               GridEditInputCell does. The outlined small field is ~40px tall, taller than a
   *               compact row (36px) and a stacked line (24px), so the cell clipped it
   *               (Aaron's C20 screenshot, 2026-10-12: Last Inspection's date cut in half).
   *               It also sets margin="none": the app themes default every FormControl's margin
   *               (tealTheme "normal" = 16px top / 8px bottom, blueTheme "dense" = 8px / 4px), which
   *               pushed the 100%-high field down a cell that clips (A824, 2026-10-14).
   */
  placement?: TenantDatePickerPlacement;
}

export type TenantDatePickerPlacement = "form" | "gridCell";

/** The gridCell input: no vertical padding, the full height of its line. */
export const GRID_CELL_DATE_INPUT_SX = { py: 0, height: "100%", boxSizing: "border-box" } as const;

/**
 * The gridCell look: no outline or underline (the grid draws the editing cell's focus), the
 * input fills the line's height with no vertical padding, the cell's font, and a calendar
 * button that fits a 24px line -- inside the field: the picker's button is edge="end" (-12px
 * right margin), which ran it 4px past the field's 8px padding into the cell's clipped edge.
 */
export const GRID_CELL_DATE_SX = {
  height: "100%",
  m: 0,
  "& .MuiInputBase-root": { height: "100%", minHeight: 0, fontSize: "inherit", px: 1 },
  "& .MuiInputAdornment-root": { ml: 0.5 },
  "& .MuiInputAdornment-root .MuiIconButton-root": { p: "2px" },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: 18 },
} as const;

const toIso = (d: Date) => format(d, "yyyy-MM-dd");

export default function TenantDatePicker(props: TenantDatePickerProps) {
  const adapter = useContext(MuiPickersAdapterContext);
  const field = <TenantDatePickerField {...props} />;
  return adapter ? field : <LocalizationProvider dateAdapter={AdapterDateFns}>{field}</LocalizationProvider>;
}

function TenantDatePickerField({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  required,
  fullWidth,
  size,
  error,
  helperText,
  textFieldProps,
  placement = "form",
  anchorToIcon = false,
}: TenantDatePickerProps) {
  const [calendarButton, setCalendarButton] = useState<HTMLButtonElement | null>(null);
  const pattern = useTenantDateFormat() ?? DEFAULT_TENANT_DATE_FORMAT;
  // The picker's own Date, so a half-typed (invalid) date survives the next
  // render instead of being reset by a parent that still holds the old value.
  const [draft, setDraft] = useState<Date | null>(() => tenantDateValue(value));

  useEffect(() => {
    const incoming = tenantDateValue(value);
    setDraft((d) => {
      const same =
        (incoming === null && d === null) ||
        (incoming !== null && d !== null && isValid(d) && toIso(d) === toIso(incoming));
      return same ? d : incoming;
    });
  }, [value]);

  return (
    <DatePicker
      label={label}
      value={draft}
      inputFormat={pattern}
      minDate={tenantDateValue(minDate) ?? undefined}
      maxDate={tenantDateValue(maxDate) ?? undefined}
      disabled={disabled}
      // gridCell: the calendar button sits inside the field (no edge="end" -12px margin).
      OpenPickerButtonProps={{ ...(placement === "gridCell" ? { edge: false as const } : {}), ...(anchorToIcon ? { ref: setCalendarButton } : {}) }}
      PopperProps={anchorToIcon ? { anchorEl: calendarButton, placement: "bottom-start" } : undefined}
      onChange={(d: Date | null) => {
        setDraft(d);
        if (d === null) onChange("");
        else if (isValid(d)) onChange(toIso(d));
        // an invalid (half-typed) date is not reported
      }}
      renderInput={(params) =>
        placement === "gridCell" ? (
          <TextField
            {...params}
            {...textFieldProps}
            // No size="small": its own input padding (paddingTop 1px) fights the line fit below.
            variant="standard"
            // Not the theme's FormControl margin (tealTheme "normal": 16px above a 100%-high field).
            margin="none"
            fullWidth={fullWidth}
            error={error ?? params.error}
            data-placement="gridCell"
            InputProps={{ ...params.InputProps, disableUnderline: true, ...textFieldProps?.InputProps }}
            // On the input's own styled component, so it replaces MUI's input padding rather
            // than competing with it by selector.
            inputProps={{ ...params.inputProps, sx: GRID_CELL_DATE_INPUT_SX, ...textFieldProps?.inputProps }}
            sx={[GRID_CELL_DATE_SX, ...(Array.isArray(textFieldProps?.sx) ? textFieldProps!.sx : textFieldProps?.sx ? [textFieldProps.sx] : [])] as any}
          />
        ) : (
          <TextField
            {...params}
            {...textFieldProps}
            required={required}
            fullWidth={fullWidth}
            size={size}
            error={error ?? params.error}
            helperText={helperText}
            InputLabelProps={{ ...params.InputLabelProps, shrink: true, ...textFieldProps?.InputLabelProps }}
            inputProps={{ ...params.inputProps, ...textFieldProps?.inputProps }}
          />
        )
      }
    />
  );
}

export interface TenantDateFieldProps extends Omit<TenantDatePickerProps, "value" | "onChange"> {
  /** The Formik field (dotted paths work: "limitations.last_date"). */
  name: string;
}

/**
 * TenantDatePicker bound to a Formik field by name — the drop-in for a raw
 * `<Field component={DatePicker} name=… />` (formik-mui-x-date-pickers), which reads the
 * wire's "YYYY-MM-DD" as UTC midnight and so shows the day before west of UTC (the SOP
 * re-walk's picker audit, 2026-10-14):
 *
 *   <TenantDateField name="date_of_values" label="Date of Values" required fullWidth />
 *
 * The field keeps the wire string: untouched, it saves as it came; picked or typed, it holds
 * "YYYY-MM-DD" ("" when cleared). A Date already in the form (a create form's "today") shows
 * as its local day and is only rewritten when the user changes it.
 */
export function TenantDateField({ name, ...props }: TenantDateFieldProps) {
  const [field, , helpers] = useField<string | Date | null | undefined>(name);
  const v = field.value;
  const value = v instanceof Date ? (isValid(v) ? toIso(v) : "") : v;
  return <TenantDatePicker {...props} value={value} onChange={(iso) => helpers.setValue(iso)} />;
}
